import { useI18n } from "@baton/i18n";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import { portalsQuery } from "../../portal/queries.ts";

/**
 * 028 FR-028-053: the reader's unread messages over every portal of the Team a portal worker reads,
 * which the workspace rail's Tickets tile draws, with its words; read only while `enabled`, which is
 * while the reader holds the Tickets mode. It reads the Tickets screens' own list of the portals,
 * every page of it, so the tile, the mode's sidebar and its landing share one cache entry, and a
 * `portal.*` event's one invalidation re-reads the count.
 */
export function useTicketsUnread(ws: string, enabled: boolean): { readonly count: number; readonly label: string } {
  const { t } = useI18n();
  const runtime = useRuntime();
  const portals = useInfiniteQuery({ ...portalsQuery(runtime, ws), enabled });
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = portals;
  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [enabled, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);
  const count = (portals.data?.pages ?? []).flatMap((page) => page.items).reduce((total, portal) => total + portal.unread_count, 0);
  const label = t("nav.tickets.unreadTotal", { count });
  // One object while the count stands, so the rail's modes are rebuilt only when it moves.
  return useMemo(() => ({ count, label }), [count, label]);
}
